import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  RefreshControl,
  Alert,
  ActivityIndicator,
  Image,
  Modal,
  TextInput,
  Platform,
  Dimensions,
} from 'react-native';
import { collection, getDocs, query, orderBy, doc, getDoc, setDoc, updateDoc } from '../services/cachedFirestore';
import { writeBatch, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import TelegramChannelFormModal from '../components/TelegramChannelFormModal';
import TelegramChannelsList from '../components/TelegramChannelsList';
import ScrapingReportModal from '../components/ScrapingReportModal';
import { NibJobsIcon } from '../assets';
import Icon from '@expo/vector-icons/MaterialIcons';
import { fonts } from '../utils/fonts';

const defaultColors = {
  beeYellow: '#F4C430',
  honeyGold: '#D4AF37',
  darkYellow: '#B8860B',
  deepNavy: '#1A1B3E',
  charcoal: '#2C2C2C',
  cream: '#FFF9E6',
  white: '#FFFFFF',
  warmGray: '#6B6B6B',
  lightGray: '#F5F5F5',
  success: '#27AE60',
  danger: '#E74C3C',
  warning: '#F39C12'
};

interface TelegramChannel {
  id: string;
  username: string;
  name: string;
  imageUrl?: string;
  category: string;
  isActive: boolean;
  scrapingEnabled: boolean;
  totalJobsScraped: number;
  lastScraped: any | null;
  createdAt: any;
  updatedAt: any;
}

export default function TelegramChannelsScreen({ colors = defaultColors, isDarkMode = false }: { colors?: any; isDarkMode?: boolean }) {
  const [channels, setChannels] = useState<TelegramChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [scrapingStatus, setScrapingStatus] = useState<'idle' | 'scraping' | 'completed' | 'error'>('idle');
  const [scrapingProgress, setScrapingProgress] = useState(0);
  const [scrapingMessage, setScrapingMessage] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [stoppingScrape, setStoppingScrape] = useState(false);
  const [dimensions, setDimensions] = useState(Dimensions.get('window'));
  const [scrapingDetails, setScrapingDetails] = useState<{
    totalChannels: number;
    processedChannels: number;
    currentChannel: string | null;
    totalJobsExtracted: number;
    errors: string[];
  }>({
    totalChannels: 0,
    processedChannels: 0,
    currentChannel: null,
    totalJobsExtracted: 0,
    errors: []
  });
  // A single manual scrape now runs as a chain of independent batch sessions (see
  // runChannelBatch on the backend) — each batch's own scrapingProgress doc only
  // reports that batch's numbers, so this ref carries the running total across
  // batches already finished, for the overall progress bar and final summary.
  const cumulativeStatsRef = useRef<{
    channelsCompleted: number;
    totalJobsExtracted: number;
    totalMessagesProcessed: number;
    errors: string[];
  }>({ channelsCompleted: 0, totalJobsExtracted: 0, totalMessagesProcessed: 0, errors: [] });

  const isMobile = dimensions.width < 768;

  // Monitor dimension changes
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);

  // Schedule configuration state
  const [scheduleConfig, setScheduleConfig] = useState<{
    enabled: boolean;
    schedules: Array<{
      id: string;
      hour: number;
      minute: number;
      days: number[];
      enabled: boolean;
      hoursBack: number; // How many hours back to scrape (default 24)
    }>;
    lastRun?: Date;
    lastRunHour?: number;
    lastRunMinute?: number;
    lastRunHoursBack?: number;
    lastRunScheduleId?: string;
  }>({
    enabled: false,
    schedules: []
  });
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<{
    id: string;
    hour: number;
    minute: number;
    days: number[];
    enabled: boolean;
    hoursBack: number;
  } | null>(null);
  const [showManualScrapeModal, setShowManualScrapeModal] = useState(false);
  const [manualScrapeHoursBack, setManualScrapeHoursBack] = useState(24);
  const [showReportsModal, setShowReportsModal] = useState(false);

  const manualScrapeChannels = httpsCallable(functions, 'runTelegramScrapingNowV2');
  const cancelScrapeSession = httpsCallable(functions, 'cancelScrapeSession');

  // Load schedule configuration
  const loadScheduleConfig = async () => {
    try {
      console.log('🔄 Loading schedule config with forceRefresh...');
      // Force refresh to always get latest schedule configuration
      const configDoc = await getDoc(doc(db, 'settings', 'scrapingSchedule'), { forceRefresh: true, ttl: 60000 });
      if (configDoc.exists()) {
        const data = configDoc.data();
        setScheduleConfig({
          enabled: data.enabled || false,
          schedules: data.schedules || [],
          lastRun: data.lastRun?.toDate(),
          lastRunHour: data.lastRunHour,
          lastRunMinute: data.lastRunMinute,
          lastRunHoursBack: data.lastRunHoursBack,
          lastRunScheduleId: data.lastRunScheduleId
        });
      }
    } catch (error) {
      console.error('Error loading schedule config:', error);
    }
  };

  // Save schedule configuration
  const saveScheduleConfig = async (newConfig: typeof scheduleConfig) => {
    try {
      await setDoc(doc(db, 'settings', 'scrapingSchedule'), {
        enabled: newConfig.enabled,
        schedules: newConfig.schedules,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setScheduleConfig(newConfig);
      Alert.alert('Success', 'Schedule configuration saved!');
    } catch (error) {
      console.error('Error saving schedule config:', error);
      Alert.alert('Error', 'Failed to save schedule configuration');
    }
  };

  // Toggle schedule enabled
  const toggleScheduleEnabled = async () => {
    const newConfig = { ...scheduleConfig, enabled: !scheduleConfig.enabled };
    await saveScheduleConfig(newConfig);
  };

  // Add new schedule
  const addSchedule = async (hour: number, minute: number, days: number[], hoursBack: number = 24) => {
    const newSchedule = {
      id: `schedule_${Date.now()}`,
      hour,
      minute,
      days,
      enabled: true,
      hoursBack
    };
    const newConfig = {
      ...scheduleConfig,
      schedules: [...scheduleConfig.schedules, newSchedule]
    };
    await saveScheduleConfig(newConfig);
    setShowScheduleModal(false);
    setEditingSchedule(null);
  };

  // Update schedule
  const updateSchedule = async (scheduleId: string, hour: number, minute: number, days: number[], enabled: boolean, hoursBack: number = 24) => {
    const newConfig = {
      ...scheduleConfig,
      schedules: scheduleConfig.schedules.map(s => 
        s.id === scheduleId ? { ...s, hour, minute, days, enabled, hoursBack } : s
      )
    };
    await saveScheduleConfig(newConfig);
    setShowScheduleModal(false);
    setEditingSchedule(null);
  };

  // Delete schedule
  const deleteSchedule = async (scheduleId: string) => {
    // Use window.confirm for web compatibility, Alert.alert for native
    const confirmDelete = () => {
      return new Promise<boolean>((resolve) => {
        if (typeof window !== 'undefined' && window.confirm) {
          resolve(window.confirm('Are you sure you want to delete this schedule?'));
        } else {
          Alert.alert(
            'Delete Schedule',
            'Are you sure you want to delete this schedule?',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Delete', style: 'destructive', onPress: () => resolve(true) }
            ]
          );
        }
      });
    };

    const confirmed = await confirmDelete();
    if (confirmed) {
      try {
        const newConfig = {
          ...scheduleConfig,
          schedules: scheduleConfig.schedules.filter(s => s.id !== scheduleId)
        };
        await saveScheduleConfig(newConfig);
      } catch (error) {
        console.error('Error deleting schedule:', error);
      }
    }
  };

  // Format hour for display
  const formatHour = (hour: number, minute: number = 0) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  // Listen for real-time progress updates
  useEffect(() => {
    if (!currentSessionId) return;

    console.log('📡 Setting up progress listener for session:', currentSessionId);
    
    const progressDocRef = doc(db, 'scrapingProgress', currentSessionId);
    const unsubscribe = onSnapshot(progressDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        console.log('📊 Progress update:', data);

        const prior = cumulativeStatsRef.current;
        const totalBatches = data.totalBatches || 1;
        const batchNumber = data.batchNumber || 1;
        const overallTotalChannels = data.overallTotalChannels || data.totalChannels || 0;
        const channelsDoneOverall = prior.channelsCompleted + (data.processedChannels || 0);
        const batchLabel = totalBatches > 1 ? ` (batch ${batchNumber}/${totalBatches})` : '';

        setScrapingDetails({
          totalChannels: overallTotalChannels,
          processedChannels: channelsDoneOverall,
          currentChannel: data.currentChannel,
          totalJobsExtracted: prior.totalJobsExtracted + (data.totalJobsExtracted || 0),
          errors: [...prior.errors, ...(data.errors || [])]
        });

        // Calculate progress percentage across the whole run, not just this batch
        const progress = overallTotalChannels > 0
          ? Math.round((channelsDoneOverall / overallTotalChannels) * 100)
          : 0;
        setScrapingProgress(progress);

        if (data.status === 'running') {
          setScrapingMessage(
            data.currentChannel
              ? `Processing ${data.currentChannel}...${batchLabel} (${channelsDoneOverall}/${overallTotalChannels})`
              : `Scraping in progress...${batchLabel} (${channelsDoneOverall}/${overallTotalChannels})`
          );
        } else if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
          // This batch's own session is done — fold its numbers into the running
          // total before deciding whether more batches remain.
          const cumulative = {
            channelsCompleted: prior.channelsCompleted + (data.totalChannels || 0),
            totalJobsExtracted: prior.totalJobsExtracted + (data.totalJobsExtracted || 0),
            totalMessagesProcessed: prior.totalMessagesProcessed + (data.totalMessagesProcessed || 0),
            errors: [...prior.errors, ...(data.errors || [])],
          };
          cumulativeStatsRef.current = cumulative;

          // A cancelled batch never has a nextSessionId (it's retracted server-side
          // the moment cancellation is detected), so this naturally stops the chain.
          const nextSessionId: string | null = data.nextSessionId || null;

          if (nextSessionId) {
            // More batches remain — chain to the next session instead of finishing up.
            setScrapingMessage(
              `Batch ${batchNumber}/${totalBatches} ${data.status === 'failed' ? 'failed' : 'complete'} (${cumulative.totalJobsExtracted} jobs so far). Starting next batch...`
            );
            setCurrentSessionId(nextSessionId);
            return;
          }

          // Last batch — finalize using totals accumulated across every batch.
          if (data.status === 'completed') {
            setScrapingMessage(`Completed! Extracted ${cumulative.totalJobsExtracted} jobs from ${cumulative.channelsCompleted} channels`);
            setScrapingStatus('completed');

            const errorDetails = cumulative.errors.length
              ? `\n\nEncountered ${cumulative.errors.length} error(s):\n${cumulative.errors.slice(0, 3).join('\n')}${cumulative.errors.length > 3 ? `\n... and ${cumulative.errors.length - 3} more` : ''}`
              : '';

            Alert.alert(
              cumulative.errors.length ? 'Scraping Completed with Warnings' : 'Scraping Complete',
              `Processed: ${cumulative.channelsCompleted} channels${totalBatches > 1 ? ` across ${totalBatches} batches` : ''}\nJobs found: ${cumulative.totalJobsExtracted}\nMessages processed: ${cumulative.totalMessagesProcessed}${errorDetails}`,
              [
                { text: 'View Console', onPress: () => console.log('Full scraping result:', cumulative) },
                { text: 'OK', style: 'default' }
              ]
            );

            // Refresh channels to show updated stats
            loadChannels();

            // Auto-hide progress after 5 seconds
            setTimeout(() => {
              setScrapingStatus('idle');
              setScrapingProgress(0);
              setScrapingMessage('');
              setCurrentSessionId(null);
              cumulativeStatsRef.current = { channelsCompleted: 0, totalJobsExtracted: 0, totalMessagesProcessed: 0, errors: [] };
            }, 5000);
          } else if (data.status === 'cancelled') {
            setScrapingMessage(`Stopped. Extracted ${cumulative.totalJobsExtracted} jobs from ${cumulative.channelsCompleted} channels before stopping.`);
            setScrapingStatus('completed');

            Alert.alert(
              'Scraping Stopped',
              `Stopped by admin.\nProcessed: ${cumulative.channelsCompleted} channels\nJobs found: ${cumulative.totalJobsExtracted}\nMessages processed: ${cumulative.totalMessagesProcessed}`,
              [{ text: 'OK' }]
            );

            loadChannels();

            setTimeout(() => {
              setScrapingStatus('idle');
              setScrapingProgress(0);
              setScrapingMessage('');
              setCurrentSessionId(null);
              cumulativeStatsRef.current = { channelsCompleted: 0, totalJobsExtracted: 0, totalMessagesProcessed: 0, errors: [] };
            }, 5000);
          } else {
            setScrapingMessage(`Scraping failed: ${data.error || 'Unknown error'}`);
            setScrapingStatus('error');

            const partialNote = cumulative.totalJobsExtracted > 0
              ? `\n\n${cumulative.totalJobsExtracted} job(s) were already saved from earlier batches before this failure.`
              : '';

            Alert.alert(
              'Scraping Failed',
              `Scraping failed: ${data.error || 'Unknown error'}${partialNote}\n\nCheck console for details.`,
              [{ text: 'OK' }]
            );

            setTimeout(() => {
              setScrapingStatus('idle');
              setScrapingProgress(0);
              setScrapingMessage('');
              setCurrentSessionId(null);
              cumulativeStatsRef.current = { channelsCompleted: 0, totalJobsExtracted: 0, totalMessagesProcessed: 0, errors: [] };
            }, 3000);
          }
        }
      }
    }, (error) => {
      console.error('❌ Progress listener error:', error);
    });

    return () => unsubscribe();
  }, [currentSessionId]);

  const loadChannels = async () => {
    try {
      console.log('🔄 Loading telegram channels with forceRefresh...');
      const channelsQuery = query(
        collection(db, 'telegramChannels'),
        orderBy('createdAt', 'desc')
      );
      
      // Force refresh to bypass cache - channels should always show latest data
      const channelsSnapshot = await getDocs(channelsQuery, { forceRefresh: true, ttl: 60000 }); // 1 minute cache
      
      console.log('📊 Raw channels data:');
      const channelsList: TelegramChannel[] = channelsSnapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`  - Channel ${doc.id}:`, {
          username: data.username,
          name: data.name,
          isActive: data.isActive,
          scrapingEnabled: data.scrapingEnabled,
          totalJobsScraped: data.totalJobsScraped,
          lastScraped: data.lastScraped
        });
        
        return {
          id: doc.id,
          username: data.username || '',
          name: data.name || '',
          imageUrl: data.imageUrl,
          category: data.category || 'general',
          isActive: data.isActive ?? true,
          scrapingEnabled: data.scrapingEnabled ?? true,
          totalJobsScraped: data.totalJobsScraped || 0,
          lastScraped: data.lastScraped || null,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        } as TelegramChannel;
      });
      
      console.log(`✅ Loaded ${channelsList.length} telegram channels from Firestore`);
      
      setChannels(channelsList);
    } catch (error) {
      console.error('Error loading channels:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadChannels();
  };

  const handleStopScraping = async () => {
    if (!currentSessionId) return;

    const confirmed = typeof window !== 'undefined' && window.confirm
      ? window.confirm("Stop this scrape? It will finish the channel it's currently on, then stop — the rest of this run will be skipped.")
      : await new Promise<boolean>((resolve) => {
          Alert.alert(
            'Stop this scrape?',
            "It will finish the channel it's currently on, then stop — the rest of this run will be skipped.",
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Stop', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmed) return;

    setStoppingScrape(true);
    try {
      await cancelScrapeSession({ sessionId: currentSessionId });
    } catch (error: any) {
      console.error('❌ Error stopping scrape:', error);
      const message = error?.message || 'Failed to stop the scrape';
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(message);
      } else {
        Alert.alert('Could Not Stop Scrape', message);
      }
    } finally {
      setStoppingScrape(false);
    }
  };

  const handleManualScrape = async (hoursBack: number = 24) => {
    cumulativeStatsRef.current = { channelsCompleted: 0, totalJobsExtracted: 0, totalMessagesProcessed: 0, errors: [] };
    setScrapingStatus('scraping');
    setScrapingProgress(0);
    setScrapingMessage(`Initializing scraper (${hoursBack} hours lookback)...`);

    try {
      console.log(`🚀 Starting manual scrape with ${hoursBack} hours lookback...`);
      const result = await manualScrapeChannels({ hoursBack });
      console.log('📊 Scraping result:', result);
      
      const data = result.data as any;
      
      // Set the session ID to start listening for progress
      if (data?.sessionId) {
        console.log('� Starting progress tracking for session:', data.sessionId);
        setCurrentSessionId(data.sessionId);
      }
      
      if (data?.success) {
        // Function returned immediately - scraping is running in background
        console.log('✅ Scraping started in background:', data);
        
        // Log any errors that occurred during scraping (will be updated via progress listener)
        if (data.errors && data.errors.length > 0) {
          console.error('⚠️ Scraping completed with errors:', data.errors);
          data.errors.forEach((err: string, index: number) => {
            console.error(`  Error ${index + 1}:`, err);
          });
        }
        
        setScrapingMessage(
          data.totalBatches > 1
            ? `Scraping ${data.channelsToProcess} channels in progress (${data.totalBatches} batches of up to 10)...`
            : `Scraping ${data.channelsToProcess} channels in progress...`
        );
        
        // Note: Final results will be shown by the progress listener when status becomes 'completed'
        
        // Refresh channels to show updated stats later
        setTimeout(() => loadChannels(), 5000);
      } else {
        setScrapingMessage('Scraping failed - check console for details');
        setScrapingStatus('error');
        
        console.error('❌ Scraping failed. Full response:', data);
        
        Alert.alert(
          'Scraping Failed',
          'The scraping process failed. Please check the console for detailed error logs.',
          [{ text: 'OK' }]
        );
        
        setTimeout(() => {
          setScrapingStatus('idle');
          setScrapingProgress(0);
          setScrapingMessage('');
        }, 3000);
      }

    } catch (error: any) {
      console.error('❌ Manual scraping failed with exception:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        stack: error.stack
      });
      
      setScrapingStatus('error');
      
      const errorMessage = error.message || String(error);
      const errorCode = error.code || 'unknown';
      const errorDetails = error.details || {};
      
      setScrapingMessage(`Error: ${errorMessage}`);
      
      Alert.alert(
        'Scraping Failed',
        `An error occurred during scraping:\n\nError: ${errorMessage}\nCode: ${errorCode}\n\nCheck console for full details.`,
        [
          { 
            text: 'View Console', 
            onPress: () => {
              console.log('=== FULL ERROR DETAILS ===');
              console.error('Error object:', error);
              console.error('Error details:', errorDetails);
            }
          },
          { text: 'OK', style: 'cancel' }
        ]
      );
      
      setTimeout(() => {
        setScrapingStatus('idle');
        setScrapingProgress(0);
        setScrapingMessage('');
      }, 5000);
    }
  };

  const handleChannelAdded = () => {
    loadChannels();
  };

  const handleChannelsUpdate = () => {
    loadChannels();
  };

  const enableAllChannelsForScraping = async () => {
    try {
      const batch = writeBatch(db);
      let updatedCount = 0;

      for (const channel of channels) {
        if (!channel.isActive || !channel.scrapingEnabled) {
          const channelRef = doc(db, 'telegramChannels', channel.id);
          batch.update(channelRef, {
            isActive: true,
            scrapingEnabled: true,
            updatedAt: serverTimestamp()
          });
          updatedCount++;
        }
      }

      if (updatedCount > 0) {
        await batch.commit();
        
        // Wait a bit for Firestore to propagate changes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Reload channels to show updated state
        await loadChannels();
        
        Alert.alert(
          'Channels Updated! ✅',
          `${updatedCount} channels have been enabled for scraping.`,
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert(
          'All Set! 👍',
          'All channels are already enabled for scraping.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Error enabling channels:', error);
      Alert.alert(
        'Error',
        'Failed to update channel settings. Please try again.',
        [{ text: 'OK' }]
      );
    }
  };

  const disableAllChannelsForScraping = async () => {
    console.log('🔴 disableAllChannelsForScraping CALLED - Button was clicked!');
    console.log('🔴 Current channels count:', channels.length);
    console.log('🔴 Showing confirmation dialog...');
    
    // Use window.confirm for web, Alert.alert for native
    const confirmed = typeof window !== 'undefined' && window.confirm 
      ? window.confirm('Disable All Channels?\n\nThis will deactivate all telegram channels and stop scraping. Are you sure?')
      : await new Promise<boolean>((resolve) => {
          Alert.alert(
            'Disable All Channels? ⚠️',
            'This will deactivate all telegram channels and stop scraping. Are you sure?',
            [
              { 
                text: 'Cancel', 
                style: 'cancel',
                onPress: () => {
                  console.log('🔴 User cancelled');
                  resolve(false);
                }
              },
              {
                text: 'Disable All',
                style: 'destructive',
                onPress: () => {
                  console.log('🔴 User confirmed via Alert');
                  resolve(true);
                }
              }
            ]
          );
        });
    
    if (!confirmed) {
      console.log('🔴 User cancelled the operation');
      return;
    }
    
    console.log('🔴 User confirmed - Starting disable process');
    console.log('🔴 Channels to process:', channels.map(c => c.username).join(', '));
    
    try {
      console.log('=== DISABLE ALL CHANNELS START ===');
      console.log('Total channels to disable:', channels.length);
      
      // Update each channel individually with error handling
      const results = await Promise.allSettled(
        channels.map(async (channel) => {
          try {
            const channelRef = doc(db, 'telegramChannels', channel.id);
            console.log(`🔴 Disabling channel ${channel.id} (${channel.username})`);
            
            await updateDoc(channelRef, {
              isActive: false,
              scrapingEnabled: false,
              updatedAt: serverTimestamp()
            });
            
            console.log(`✅ Successfully disabled ${channel.username}`);
            return { success: true, channel: channel.username };
          } catch (error) {
            console.error(`❌ Failed to disable ${channel.username}:`, error);
            return { success: false, channel: channel.username, error };
          }
        })
      );
      
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)).length;
      
      console.log(`=== DISABLE ALL CHANNELS COMPLETE ===`);
      console.log(`✅ Successful: ${successful}, ❌ Failed: ${failed}`);
      
      // Wait for Firestore to propagate
      console.log('🔴 Waiting 800ms for Firestore to propagate...');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Reload channels
      console.log('🔴 Reloading channels...');
      await loadChannels();
      console.log('🔴 Channels reloaded successfully');
      
      if (failed === 0) {
        alert(`Channels Disabled!\n\nAll ${successful} channels have been deactivated.`);
      } else {
        alert(`Partially Completed\n\n${successful} channels disabled, ${failed} failed. Check console for details.`);
      }
    } catch (error) {
      console.error('❌ Error disabling channels:', error);
      alert('Error: Failed to update channel settings. Please try again.');
    }
  };

  useEffect(() => {
    loadChannels();
    loadScheduleConfig();
  }, []);

  const activeCount = channels.filter(c => c.isActive && c.scrapingEnabled).length;
  const totalJobsScraped = channels.reduce((total, ch) => total + ch.totalJobsScraped, 0);

  return (
    <View style={{ flex: 1, backgroundColor: colors.adminPageBg }}>
      {/* ── Page Header ── */}
      <View style={{
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        backgroundColor: colors.adminHeaderBg,
        paddingHorizontal: isMobile ? 16 : 28,
        paddingVertical: isMobile ? 14 : 18,
        gap: isMobile ? 12 : 0
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: isMobile ? 10 : 14 }}>
          <View style={{
            width: isMobile ? 36 : 40,
            height: isMobile ? 36 : 40,
            borderRadius: 11,
            backgroundColor: `${colors.sidebarAccent}22`,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <Icon name="send" size={isMobile ? 18 : 20} color={colors.sidebarAccent} />
          </View>
          <View>
            <Text style={{
              fontSize: isMobile ? 16 : 18,
              fontFamily: fonts.bold,
              fontWeight: '700',
              color: '#F9FAFB',
              letterSpacing: -0.3
            }}>
              Telegram Channels
            </Text>
            <Text style={{
              fontSize: isMobile ? 10 : 11,
              fontFamily: fonts.regular,
              color: '#64748B',
              marginTop: 1
            }}>
              {channels.length} channels · {activeCount} active
            </Text>
          </View>
        </View>
        <View style={{
          flexDirection: 'row',
          gap: 8,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          width: isMobile ? '100%' : 'auto'
        }}>
          <TouchableOpacity
            onPress={() => setShowReportsModal(true)}
            style={{
              flex: isMobile ? 1 : undefined,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              backgroundColor: '#3B82F622',
              paddingHorizontal: isMobile ? 10 : 12,
              paddingVertical: isMobile ? 7 : 8,
              borderRadius: 9,
              borderWidth: 1,
              borderColor: '#3B82F640'
            }}
          >
            <Icon name="assessment" size={isMobile ? 14 : 15} color="#3B82F6" />
            <Text style={{
              fontSize: isMobile ? 11 : 12,
              fontFamily: fonts.semiBold,
              fontWeight: '600',
              color: '#3B82F6'
            }}>Reports</Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setShowAddModal(true)}
            style={{
              flex: isMobile ? 1 : undefined,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              backgroundColor: colors.sidebarAccent,
              paddingHorizontal: isMobile ? 10 : 14,
              paddingVertical: isMobile ? 7 : 8,
              borderRadius: 9
            }}
          >
            <Icon name="add" size={isMobile ? 14 : 15} color="#FFFFFF" />
            <Text style={{
              fontSize: isMobile ? 11 : 12,
              fontFamily: fonts.semiBold,
              fontWeight: '600',
              color: '#FFFFFF'
            }}>Add Channel</Text>
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: isMobile ? 16 : 24, gap: 20 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={['#3B82F6']} tintColor="#3B82F6" />
        }
      >
        {/* ── Overview Stats ── */}
        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 16 }}>
          {[
            { icon: 'layers', label: 'Total Channels', value: channels.length, accent: '#3B82F6' },
            { icon: 'check-circle', label: 'Active Channels', value: activeCount, accent: '#10B981' },
            { icon: 'work', label: 'Jobs Scraped', value: totalJobsScraped.toLocaleString(), accent: '#F59E0B' },
          ].map((card) => (
            <View key={card.label} style={{
              flex: 1,
              backgroundColor: colors.adminCardBg,
              borderRadius: 14, padding: 18,
              borderTopWidth: 3, borderTopColor: card.accent,
              ...Platform.select({
                web: { boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.06)' },
                default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
              }),
            }}>
              <View style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: card.accent + '20', justifyContent: 'center', alignItems: 'center', marginBottom: 12 }}>
                <Icon name={card.icon as any} size={20} color={card.accent} />
              </View>
              <Text style={{ fontSize: 24, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive }}>
                {card.value}
              </Text>
              <Text style={{ fontSize: 11, fontFamily: fonts.medium, fontWeight: '500', color: colors.adminMuted, marginTop: 3 }}>
                {card.label}
              </Text>
            </View>
          ))}
        </View>

        {/* ── Quick Actions ── */}
        <View style={{
          backgroundColor: colors.adminCardBg,
          borderRadius: 14, overflow: 'hidden',
          ...Platform.select({
            web: { boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.06)' },
            default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
          }),
        }}>
          {/* Section label */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: colors.adminHeaderBg, paddingHorizontal: 18, paddingVertical: 12, borderLeftWidth: 4, borderLeftColor: '#F59E0B' }}>
            <Icon name="bolt" size={16} color="#F59E0B" />
            <Text style={{ fontSize: 11, fontFamily: fonts.bold, fontWeight: '700', color: '#F59E0B', letterSpacing: 1.2, textTransform: 'uppercase' }}>Quick Actions</Text>
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', padding: 16, gap: 10 }}>
            {[
              { icon: 'refresh', label: 'Refresh', color: '#10B981', onPress: handleRefresh },
              { icon: scrapingStatus === 'scraping' ? 'hourglass-empty' : 'play-arrow', label: scrapingStatus === 'scraping' ? 'Scraping…' : 'Start Scrape', color: '#F59E0B', onPress: scrapingStatus === 'idle' ? () => setShowManualScrapeModal(true) : undefined, disabled: scrapingStatus === 'scraping' },
              { icon: 'check-circle', label: 'Enable All', color: '#10B981', onPress: enableAllChannelsForScraping },
              { icon: 'cancel', label: 'Disable All', color: '#EF4444', onPress: disableAllChannelsForScraping },
            ].map((action) => (
              <TouchableOpacity
                key={action.label}
                onPress={action.onPress}
                disabled={action.disabled}
                style={{
                  flexDirection: 'row', alignItems: 'center', gap: 7,
                  backgroundColor: action.color + '18',
                  borderWidth: 1, borderColor: action.color + '35',
                  paddingHorizontal: 14, paddingVertical: 10, borderRadius: 10,
                  opacity: action.disabled ? 0.5 : 1,
                }}
              >
                {action.disabled
                  ? <ActivityIndicator size="small" color={action.color} />
                  : <Icon name={action.icon as any} size={16} color={action.color} />
                }
                <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, fontWeight: '600', color: action.color }}>
                  {action.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* ── Scraping Progress ── */}
        {scrapingStatus !== 'idle' && (
          <View style={{
            backgroundColor: colors.adminCardBg,
            borderRadius: 14, overflow: 'hidden',
            ...Platform.select({
              web: { boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.06)' },
              default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
            }),
          }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: colors.adminHeaderBg, paddingHorizontal: 18, paddingVertical: 12, borderLeftWidth: 4, borderLeftColor: scrapingStatus === 'error' ? '#EF4444' : '#10B981' }}>
              <Icon name={scrapingStatus === 'error' ? 'error-outline' : 'sync'} size={16} color={scrapingStatus === 'error' ? '#EF4444' : '#10B981'} />
              <Text style={{ fontSize: 11, fontFamily: fonts.bold, fontWeight: '700', color: scrapingStatus === 'error' ? '#EF4444' : '#10B981', letterSpacing: 1.2, textTransform: 'uppercase' }}>Scraping Status</Text>
              <Text style={{ marginLeft: 'auto' as any, fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: scrapingStatus === 'error' ? '#EF4444' : '#10B981' }}>{scrapingProgress}%</Text>
              {scrapingStatus === 'scraping' && currentSessionId && (
                <TouchableOpacity
                  onPress={handleStopScraping}
                  disabled={stoppingScrape}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginLeft: 10, paddingHorizontal: 9, paddingVertical: 4, borderRadius: 20, backgroundColor: '#EF444420', opacity: stoppingScrape ? 0.6 : 1 }}
                >
                  {stoppingScrape ? (
                    <ActivityIndicator size="small" color="#EF4444" />
                  ) : (
                    <>
                      <Icon name="stop-circle" size={12} color="#EF4444" />
                      <Text style={{ fontSize: 11, fontWeight: '600', color: '#EF4444' }}>Stop</Text>
                    </>
                  )}
                </TouchableOpacity>
              )}
            </View>
            <View style={{ padding: 18, gap: 12 }}>
              {/* Progress track */}
              <View style={{ height: 6, borderRadius: 3, backgroundColor: colors.sidebarHover, overflow: 'hidden' }}>
                <View style={{ height: '100%', width: `${scrapingProgress}%` as any, borderRadius: 3, backgroundColor: scrapingStatus === 'error' ? '#EF4444' : '#10B981' }} />
              </View>
              <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>{scrapingMessage}</Text>
              {scrapingStatus === 'scraping' && scrapingDetails.totalChannels > 0 && (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                  {[
                    { label: 'Channels', value: `${scrapingDetails.processedChannels}/${scrapingDetails.totalChannels}` },
                    { label: 'Jobs Found', value: String(scrapingDetails.totalJobsExtracted) },
                    scrapingDetails.errors?.length ? { label: 'Errors', value: String(scrapingDetails.errors.length), warn: true } : null,
                  ].filter(Boolean).map((stat) => stat && (
                    <View key={stat.label} style={{ backgroundColor: colors.sidebarHover, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 }}>
                      <Text style={{ fontSize: 10, fontFamily: fonts.regular, color: colors.adminMuted }}>{stat.label}</Text>
                      <Text style={{ fontSize: 14, fontFamily: fonts.bold, fontWeight: '700', color: stat.warn ? '#EF4444' : (colors.sidebarTextActive) }}>{stat.value}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
        )}

        {/* ── Auto-Scraping Schedule ── */}
        <View style={{
          backgroundColor: colors.adminCardBg,
          borderRadius: 14, overflow: 'hidden',
          ...Platform.select({
            web: { boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.06)' },
            default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
          }),
        }}>
          {/* Section label with toggle */}
          <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: colors.adminHeaderBg, paddingHorizontal: 18, paddingVertical: 12, borderLeftWidth: 4, borderLeftColor: '#6366F1' }}>
            <Icon name="schedule" size={16} color="#6366F1" />
            <Text style={{ flex: 1, fontSize: 11, fontFamily: fonts.bold, fontWeight: '700', color: '#6366F1', letterSpacing: 1.2, textTransform: 'uppercase', marginLeft: 10 }}>Auto-Scraping Schedule</Text>
            <TouchableOpacity
              onPress={toggleScheduleEnabled}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: scheduleConfig.enabled ? '#10B98140' : '#64748B40', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 }}
            >
              <View style={{ width: 7, height: 7, borderRadius: 3.5, backgroundColor: scheduleConfig.enabled ? '#10B981' : '#64748B' }} />
              <Text style={{ fontSize: 11, fontFamily: fonts.semiBold, fontWeight: '600', color: scheduleConfig.enabled ? '#10B981' : '#64748B' }}>
                {scheduleConfig.enabled ? 'Enabled' : 'Disabled'}
              </Text>
            </TouchableOpacity>
          </View>

          <View style={{ padding: 18, gap: 14 }}>
            <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.adminMuted }}>
              Times are in Ethiopia timezone (EAT). The cloud function checks every hour and triggers scraping on match.
            </Text>

            {/* Active cloud function badge */}
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: isDarkMode ? '#1E293B' : '#EFF6FF', borderRadius: 10, padding: 12, borderWidth: 1, borderColor: isDarkMode ? '#2D3D55' : '#BFDBFE' }}>
              <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#10B981' }} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: isDarkMode ? '#93C5FD' : '#1D4ED8' }}>dynamicScheduleChecker</Text>
                <Text style={{ fontSize: 10, fontFamily: fonts.regular, color: colors.adminMuted, marginTop: 1 }}>Every hour at :00 · 512MB · checks custom schedules</Text>
              </View>
              <View style={{ backgroundColor: '#1D4ED8', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 5 }}>
                <Text style={{ fontSize: 10, fontFamily: fonts.bold, fontWeight: '700', color: '#FFFFFF' }}>v2</Text>
              </View>
            </View>

            {/* Last run */}
            {scheduleConfig.lastRun && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: isDarkMode ? '#0A2010' : '#F0FDF4', borderRadius: 10, padding: 12, borderWidth: 1, borderColor: isDarkMode ? '#14432A' : '#86EFAC' }}>
                <Icon name="check-circle" size={16} color="#10B981" />
                <View>
                  <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '600', color: '#10B981' }}>
                    Last run: {scheduleConfig.lastRun.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </Text>
                  <Text style={{ fontSize: 10, fontFamily: fonts.regular, color: isDarkMode ? '#34594A' : '#6EE7B7', marginTop: 1 }}>
                    {scheduleConfig.lastRunHour}:{(scheduleConfig.lastRunMinute || 0).toString().padStart(2, '0')} · {scheduleConfig.lastRunHoursBack || 24}h lookback
                  </Text>
                </View>
              </View>
            )}

            {/* Schedule list */}
            {scheduleConfig.schedules.length === 0 ? (
              <View style={{ padding: 28, alignItems: 'center', gap: 10, borderWidth: 1, borderColor: colors.adminBorder, borderRadius: 12, borderStyle: 'dashed' }}>
                <Icon name="alarm-off" size={36} color={colors.adminMuted} />
                <Text style={{ fontSize: 13, fontFamily: fonts.regular, color: colors.adminMuted, textAlign: 'center' }}>
                  No schedules configured yet.{'\n'}Add one to automate scraping.
                </Text>
              </View>
            ) : (
              <View style={{ gap: 10 }}>
                {scheduleConfig.schedules.map((schedule) => (
                  <View key={schedule.id} style={{
                    backgroundColor: colors.sidebarHover,
                    borderRadius: 12, padding: 14,
                    borderLeftWidth: 3, borderLeftColor: schedule.enabled ? '#10B981' : '#64748B',
                  }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                        <View style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: (schedule.enabled ? '#10B981' : '#64748B') + '22', justifyContent: 'center', alignItems: 'center' }}>
                          <Icon name="alarm" size={20} color={schedule.enabled ? '#10B981' : '#64748B'} />
                        </View>
                        <View>
                          <Text style={{ fontSize: 14, fontFamily: fonts.bold, fontWeight: '700', color: colors.sidebarTextActive }}>
                            {formatHour(schedule.hour, schedule.minute || 0)}
                          </Text>
                          <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted, marginTop: 1 }}>
                            Daily · {schedule.hoursBack >= 24 ? `${Math.round(schedule.hoursBack / 24)}d` : `${schedule.hoursBack}h`} lookback
                          </Text>
                        </View>
                      </View>
                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <TouchableOpacity
                          onPress={() => { setEditingSchedule(schedule); setShowScheduleModal(true); }}
                          style={{ padding: 8, backgroundColor: isDarkMode ? '#2D3D55' : '#E2E8F0', borderRadius: 8 }}
                        >
                          <Icon name="edit" size={16} color={colors.adminMuted} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => deleteSchedule(schedule.id)}
                          style={{ padding: 8, backgroundColor: '#EF444420', borderRadius: 8 }}
                        >
                          <Icon name="delete" size={16} color="#EF4444" />
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                ))}
              </View>
            )}

            <TouchableOpacity
              onPress={() => { setEditingSchedule(null); setShowScheduleModal(true); }}
              style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: '#6366F1', paddingVertical: 13, borderRadius: 10 }}
            >
              <Icon name="add" size={16} color="#FFFFFF" />
              <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, fontWeight: '600', color: '#FFFFFF' }}>Add Schedule</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* ── Channels List ── */}
        <View style={{
          backgroundColor: colors.adminCardBg,
          borderRadius: 14, overflow: 'hidden',
          ...Platform.select({
            web: { boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.06)' },
            default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 2 },
          }),
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: colors.adminHeaderBg, paddingHorizontal: 18, paddingVertical: 12, borderLeftWidth: 4, borderLeftColor: '#3B82F6' }}>
            <Icon name="chat" size={16} color="#3B82F6" />
            <Text style={{ flex: 1, fontSize: 11, fontFamily: fonts.bold, fontWeight: '700', color: '#3B82F6', letterSpacing: 1.2, textTransform: 'uppercase' }}>Channels</Text>
            <View style={{ backgroundColor: '#3B82F622', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 }}>
              <Text style={{ fontSize: 11, fontFamily: fonts.bold, fontWeight: '700', color: '#3B82F6' }}>{channels.length}</Text>
            </View>
          </View>
          <TelegramChannelsList
            channels={channels}
            onChannelsUpdate={handleChannelsUpdate}
            loading={loading}
            colors={colors}
            isDarkMode={isDarkMode}
          />
        </View>
      </ScrollView>

      {/* ── Modals ── */}
      <TelegramChannelFormModal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onChannelAdded={handleChannelAdded}
        colors={colors}
      />

      {/* Manual Scrape Modal */}
      <Modal visible={showManualScrapeModal} animationType="fade" transparent onRequestClose={() => setShowManualScrapeModal(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 24 }}>
          <View style={{
            backgroundColor: colors.adminCardBg,
            borderRadius: 18, width: '100%', maxWidth: 420, overflow: 'hidden',
          }}>
            <View style={{ backgroundColor: colors.adminHeaderBg, padding: 20 }}>
              <Text style={{ fontSize: 16, fontFamily: fonts.bold, fontWeight: '700', color: '#F9FAFB' }}>Start Manual Scrape</Text>
              <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: '#64748B', marginTop: 3 }}>
                Select how far back to look for messages
              </Text>
            </View>
            <View style={{ padding: 20, gap: 16 }}>
              <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: colors.adminMuted, letterSpacing: 1, textTransform: 'uppercase' }}>
                Lookback Period
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {[
                  { label: '24h', value: 24 },
                  { label: '48h', value: 48 },
                  { label: '72h', value: 72 },
                  { label: '7 days', value: 168 },
                  { label: '14 days', value: 336 },
                  { label: '30 days', value: 720 },
                ].map(option => (
                  <TouchableOpacity
                    key={option.value}
                    onPress={() => setManualScrapeHoursBack(option.value)}
                    style={{
                      paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8,
                      backgroundColor: manualScrapeHoursBack === option.value ? '#F59E0B' : (colors.sidebarHover),
                      borderWidth: 1, borderColor: manualScrapeHoursBack === option.value ? '#D97706' : 'transparent',
                    }}
                  >
                    <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, fontWeight: '600', color: manualScrapeHoursBack === option.value ? '#000000' : (colors.adminMuted) }}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              <View style={{ flexDirection: 'row', gap: 10, marginTop: 4 }}>
                <TouchableOpacity
                  onPress={() => setShowManualScrapeModal(false)}
                  style={{ flex: 1, paddingVertical: 13, borderRadius: 10, backgroundColor: colors.sidebarHover, alignItems: 'center' }}
                >
                  <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, fontWeight: '600', color: colors.adminMuted }}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => { setShowManualScrapeModal(false); handleManualScrape(manualScrapeHoursBack); }}
                  style={{ flex: 1, paddingVertical: 13, borderRadius: 10, backgroundColor: '#F59E0B', alignItems: 'center' }}
                >
                  <Text style={{ fontSize: 13, fontFamily: fonts.bold, fontWeight: '700', color: '#000000' }}>Start Scraping</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>

      {/* Schedule Modal */}
      <Modal
        visible={showScheduleModal}
        animationType="fade"
        transparent
        onRequestClose={() => { setShowScheduleModal(false); setEditingSchedule(null); }}
      >
        <ScheduleForm
          colors={colors}
          initialHour={editingSchedule?.hour ?? 9}
          initialMinute={editingSchedule?.minute ?? 0}
          initialEnabled={editingSchedule?.enabled ?? true}
          initialHoursBack={editingSchedule?.hoursBack ?? 24}
          isEditing={!!editingSchedule}
          onSave={(hour, minute, enabled, hoursBack) => {
            if (editingSchedule) {
              updateSchedule(editingSchedule.id, hour, minute, [], enabled, hoursBack);
            } else {
              addSchedule(hour, minute, [], hoursBack);
            }
          }}
          onCancel={() => { setShowScheduleModal(false); setEditingSchedule(null); }}
        />
      </Modal>

      <ScrapingReportModal
        visible={showReportsModal}
        onClose={() => setShowReportsModal(false)}
        colors={colors}
        isDarkMode={isDarkMode}
      />
    </View>
  );
}

// Schedule Form Component - Clean Analog Clock Design
function ScheduleForm({ 
  colors, 
  initialHour, 
  initialMinute,
  initialEnabled,
  initialHoursBack,
  isEditing,
  onSave, 
  onCancel 
}: { 
  colors: any; 
  initialHour: number; 
  initialMinute: number;
  initialEnabled: boolean;
  initialHoursBack: number;
  isEditing: boolean;
  onSave: (hour: number, minute: number, enabled: boolean, hoursBack: number) => void;
  onCancel: () => void;
}) {
  const [selectedHour, setSelectedHour] = useState(initialHour % 12 || 12);
  const [selectedMinute] = useState(0); // Fixed to :00
  const [isAM, setIsAM] = useState(initialHour < 12);
  const [enabled, setEnabled] = useState(initialEnabled);
  const [hoursBack, setHoursBack] = useState(initialHoursBack);
  const [hoursBackInput, setHoursBackInput] = useState(initialHoursBack.toString());

  // Get actual 24-hour format
  const getActualHour = () => {
    if (isAM) {
      return selectedHour === 12 ? 0 : selectedHour;
    } else {
      return selectedHour === 12 ? 12 : selectedHour + 12;
    }
  };

  const formatSelectedTime = () => {
    const ampm = isAM ? 'AM' : 'PM';
    return `${selectedHour}:${selectedMinute.toString().padStart(2, '0')} ${ampm}`;
  };

  // Calculate next run time
  const getNextRunTime = () => {
    const now = new Date();
    // Convert to Ethiopia time
    const ethiopiaOffset = 3 * 60; // UTC+3
    const localOffset = now.getTimezoneOffset();
    const ethiopiaNow = new Date(now.getTime() + (ethiopiaOffset + localOffset) * 60 * 1000);
    
    const scheduledHour = getActualHour();
    const scheduledMinute = selectedMinute;
    
    // Create next run date in Ethiopia time
    let nextRun = new Date(ethiopiaNow);
    nextRun.setHours(scheduledHour, scheduledMinute, 0, 0);
    
    // If the scheduled time has already passed today, schedule for tomorrow
    if (nextRun <= ethiopiaNow) {
      nextRun.setDate(nextRun.getDate() + 1);
    }
    
    // Calculate time difference
    const diffMs = nextRun.getTime() - ethiopiaNow.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    // Format the next run date
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayName = days[nextRun.getDay()];
    const monthName = months[nextRun.getMonth()];
    const date = nextRun.getDate();
    
    return {
      dateStr: `${dayName}, ${monthName} ${date}`,
      timeUntil: diffHours > 0 ? `${diffHours}h ${diffMinutes}m` : `${diffMinutes}m`,
      isToday: nextRun.getDate() === ethiopiaNow.getDate(),
    };
  };

  // Format hours to days display
  const formatHoursToDays = (hours: number) => {
    if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''}`;
    }
    const days = hours / 24;
    if (days === Math.floor(days)) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    }
    return `${days.toFixed(1)} days`;
  };

  // Handle hours back input change
  const handleHoursBackChange = (text: string) => {
    // Only allow numbers
    const numericText = text.replace(/[^0-9]/g, '');
    setHoursBackInput(numericText);
    const numValue = parseInt(numericText) || 0;
    setHoursBack(numValue);
  };

  // Analog clock positions for hours (12 hours around the circle)
  const clockRadius = 100;
  const clockHours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  
  const getClockPosition = (index: number, total: number, radius: number) => {
    const angle = ((index - 3) * (360 / total)) * (Math.PI / 180);
    return {
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
    };
  };

  // Calculate hour hand angle
  const hourHandAngle = ((selectedHour % 12) / 12) * 360 - 90;
  // Calculate minute hand angle
  const minuteHandAngle = (selectedMinute / 60) * 360 - 90;

  const nextRun = getNextRunTime();

  return (
    <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }}>
      <ScrollView 
        contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}
        showsVerticalScrollIndicator={false}
      >
        <View style={{
          backgroundColor: '#FFFFFF',
          borderRadius: 24,
          width: '100%',
          maxWidth: 360,
          paddingVertical: 24,
          paddingHorizontal: 20,
        }}>
          {/* Header */}
          <View style={{ alignItems: 'center', marginBottom: 20 }}>
            <Text style={{ fontSize: 20, fontWeight: '700', color: '#1A1A2E' }}>
              {isEditing ? 'Edit Schedule' : 'New Schedule'}
            </Text>
            <Text style={{ fontSize: 14, color: '#888', marginTop: 4 }}>
              Set scraping time (Ethiopia Time)
            </Text>
          </View>

          {/* Analog Clock Face */}
          <View style={{ alignItems: 'center', marginBottom: 16 }}>
            <View style={{
              width: clockRadius * 2 + 36,
              height: clockRadius * 2 + 36,
              borderRadius: clockRadius + 18,
              backgroundColor: '#F8F9FA',
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 3,
              borderColor: '#E8E8E8',
            }}>
              {/* Clock Numbers */}
              {clockHours.map((hour, index) => {
                const pos = getClockPosition(index, 12, clockRadius - 8);
                const isSelected = selectedHour === hour;
                return (
                  <TouchableOpacity
                    key={hour}
                    onPress={() => setSelectedHour(hour)}
                    style={{
                      position: 'absolute',
                      left: clockRadius + 18 + pos.x - 16,
                      top: clockRadius + 18 + pos.y - 16,
                      width: 32,
                      height: 32,
                      borderRadius: 16,
                      backgroundColor: isSelected ? colors.beeYellow : 'transparent',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text style={{
                      fontSize: 14,
                      fontWeight: isSelected ? '700' : '500',
                      color: isSelected ? '#000' : '#333',
                    }}>
                      {hour}
                    </Text>
                  </TouchableOpacity>
                );
              })}
              
              {/* Center dot */}
              <View style={{
                width: 10,
                height: 10,
                borderRadius: 5,
                backgroundColor: '#1A1A2E',
                position: 'absolute',
              }} />

              {/* Hour hand */}
              <View style={{
                position: 'absolute',
                width: 45,
                height: 4,
                backgroundColor: '#1A1A2E',
                borderRadius: 2,
                transform: [{ rotate: `${hourHandAngle}deg` }],
                transformOrigin: 'left center',
                left: clockRadius + 18,
                top: clockRadius + 16,
              }} />

              {/* Minute hand */}
              <View style={{
                position: 'absolute',
                width: 60,
                height: 3,
                backgroundColor: colors.beeYellow,
                borderRadius: 2,
                transform: [{ rotate: `${minuteHandAngle}deg` }],
                transformOrigin: 'left center',
                left: clockRadius + 18,
                top: clockRadius + 16.5,
              }} />
            </View>
          </View>

          {/* Time Display */}
          <View style={{ 
            alignItems: 'center', 
            marginBottom: 12,
            backgroundColor: '#F8F9FA',
            paddingVertical: 10,
            borderRadius: 12,
          }}>
            <Text style={{ fontSize: 28, fontWeight: '300', color: '#1A1A2E' }}>
              {formatSelectedTime()}
            </Text>
          </View>

          {/* Next Run Info */}
          <View style={{
            backgroundColor: '#E8F5E9',
            padding: 12,
            borderRadius: 10,
            marginBottom: 16,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <Icon name="schedule" size={18} color="#4CAF50" />
            <Text style={{ fontSize: 13, color: '#2E7D32', marginLeft: 8, fontWeight: '500' }}>
              Next run: {nextRun.isToday ? 'Today' : nextRun.dateStr} • in {nextRun.timeUntil}
            </Text>
          </View>

          {/* Minute Selection - Fixed to :00 only */}
          <View style={{ marginBottom: 14 }}>
            <Text style={{ fontSize: 12, color: '#888', marginBottom: 6, fontWeight: '500' }}>
              Minutes (fixed at :00)
            </Text>
            <View style={{ flexDirection: 'row', gap: 6 }}>
              <View
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: colors.beeYellow,
                  alignItems: 'center',
                }}
              >
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: '#000',
                }}>
                  :00
                </Text>
              </View>
            </View>
            <Text style={{ fontSize: 11, color: '#888', marginTop: 4, fontStyle: 'italic' }}>
              Schedules only run on the hour (:00) to match the checker's timing
            </Text>
          </View>

          {/* AM/PM Toggle */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 12, color: '#888', marginBottom: 6, fontWeight: '500' }}>
              Period
            </Text>
            <View style={{ flexDirection: 'row', gap: 6 }}>
              <TouchableOpacity
                onPress={() => setIsAM(true)}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: isAM ? '#1A1A2E' : '#F0F0F0',
                  alignItems: 'center',
                }}
              >
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: isAM ? '#FFF' : '#555',
                }}>
                  AM
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setIsAM(false)}
                style={{
                  flex: 1,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: !isAM ? '#1A1A2E' : '#F0F0F0',
                  alignItems: 'center',
                }}
              >
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: !isAM ? '#FFF' : '#555',
                }}>
                  PM
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Lookback Period - Hours Input */}
          <View style={{ marginBottom: 16 }}>
            <Text style={{ fontSize: 12, color: '#888', marginBottom: 6, fontWeight: '500' }}>
              Scrape messages from the last
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <TextInput
                style={{
                  width: 80,
                  backgroundColor: '#F8F9FA',
                  borderRadius: 10,
                  paddingHorizontal: 12,
                  paddingVertical: 12,
                  fontSize: 18,
                  fontWeight: '600',
                  color: '#1A1A2E',
                  borderWidth: 2,
                  borderColor: '#E8E8E8',
                  textAlign: 'center',
                }}
                value={hoursBackInput}
                onChangeText={handleHoursBackChange}
                keyboardType="numeric"
                placeholder="24"
                placeholderTextColor="#CCC"
              />
              <Text style={{ fontSize: 14, color: '#666' }}>hours</Text>
              <View style={{ 
                backgroundColor: colors.beeYellow + '30',
                paddingHorizontal: 12,
                paddingVertical: 10,
                borderRadius: 10,
                marginLeft: 'auto',
              }}>
                <Text style={{ fontSize: 13, fontWeight: '600', color: '#1A1A2E' }}>
                  = {hoursBack > 0 ? formatHoursToDays(hoursBack) : '0 hours'}
                </Text>
              </View>
            </View>
            <Text style={{ fontSize: 11, color: '#AAA', marginTop: 6 }}>
              💡 e.g., 24 = 1 day, 168 = 7 days, 720 = 30 days
            </Text>
          </View>

          {/* Quick Presets */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {[
              { label: '4h', value: 4 },
              { label: '8h', value: 8 },
              { label: '1d', value: 24 },
              { label: '3d', value: 72 },
              { label: '1w', value: 168 },
              { label: '2w', value: 336 },
              { label: '1m', value: 720 },
            ].map((preset) => (
              <TouchableOpacity
                key={preset.value}
                onPress={() => {
                  setHoursBack(preset.value);
                  setHoursBackInput(preset.value.toString());
                }}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 14,
                  backgroundColor: hoursBack === preset.value ? colors.beeYellow : '#F0F0F0',
                }}
              >
                <Text style={{
                  fontSize: 12,
                  fontWeight: hoursBack === preset.value ? '600' : '400',
                  color: hoursBack === preset.value ? '#000' : '#666',
                }}>
                  {preset.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Enable/Disable Toggle */}
          <TouchableOpacity
            onPress={() => setEnabled(!enabled)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#F8F9FA',
              padding: 12,
              borderRadius: 10,
              marginBottom: 20,
            }}
          >
            <Text style={{ fontSize: 14, color: '#333', fontWeight: '500' }}>
              Schedule Active
            </Text>
            <View style={{
              width: 44,
              height: 26,
              borderRadius: 13,
              backgroundColor: enabled ? '#4CAF50' : '#CCC',
              justifyContent: 'center',
              padding: 2,
            }}>
              <View style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                backgroundColor: '#FFF',
                alignSelf: enabled ? 'flex-end' : 'flex-start',
              }} />
            </View>
          </TouchableOpacity>

          {/* Action Buttons */}
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <TouchableOpacity
              onPress={onCancel}
              style={{
                flex: 1,
                paddingVertical: 14,
                borderRadius: 12,
                backgroundColor: '#F0F0F0',
                alignItems: 'center',
              }}
            >
              <Text style={{ fontSize: 15, fontWeight: '600', color: '#666' }}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => onSave(getActualHour(), selectedMinute, enabled, hoursBack || 24)}
              style={{
                flex: 1,
                paddingVertical: 14,
                borderRadius: 12,
                backgroundColor: colors.beeYellow,
                alignItems: 'center',
              }}
            >
              <Text style={{ fontSize: 15, fontWeight: '600', color: '#000' }}>
                {isEditing ? 'Update' : 'Save'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  headerSubtitle: {
    fontSize: 14,
    marginTop: 2,
  },
  addButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  reportsButton: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  overviewSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 16,
    letterSpacing: -0.5,
  },
  overviewCards: {
    flexDirection: 'row',
    gap: 16,
  },
  overviewCard: {
    flex: 1,
    padding: 24,
    borderRadius: 16,
    alignItems: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.04)',
  },
  overviewCardPrimary: {
    backgroundColor: '#FFF9E6',
    borderWidth: 2,
  },
  overviewCardSuccess: {
    backgroundColor: '#F0FAF4',
    borderWidth: 2,
  },
  overviewCardInfo: {
    backgroundColor: '#FFF8EC',
    borderWidth: 2,
  },
  overviewIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  overviewValue: {
    fontSize: 32,
    fontWeight: '700',
    marginBottom: 4,
    letterSpacing: -1,
  },
  overviewLabel: {
    fontSize: 13,
    fontWeight: '500',
    textAlign: 'center',
  },
  quickActionsSection: {
    marginBottom: 32,
  },
  quickActions: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
  },
  actionButton: {
    flex: 1,
    minWidth: 150,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 14,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 5,
    borderWidth: 1,
  },
  actionButtonPrimary: {
    backgroundColor: '#FFF9E6',
  },
  actionButtonSecondary: {
    backgroundColor: '#FFF8EC',
  },
  actionButtonWarning: {
    backgroundColor: '#FFF8E6',
  },
  actionButtonSuccess: {
    backgroundColor: '#F0FAF4',
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  actionButtonContent: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  actionIconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButtonText: {
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center',
    letterSpacing: 0.3,
  },
  progressSection: {
    padding: 20,
    marginBottom: 24,
    borderRadius: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
  },
  progressTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
    letterSpacing: 0.5,
  },
  progressContainer: {
    height: 12,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 12,
    position: 'relative',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
  },
  progressBar: {
    height: '100%',
    borderRadius: 8,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.6,
    shadowRadius: 6,
  },
  progressPercentage: {
    position: 'absolute',
    right: 8,
    top: -1,
    fontSize: 12,
    fontWeight: '700',
    textShadowColor: 'rgba(255, 255, 255, 0.8)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  progressMessage: {
    fontSize: 14,
    marginBottom: 16,
    textAlign: 'center',
    fontWeight: '500',
  },
  progressDetails: {
    padding: 16,
    borderRadius: 12,
    marginTop: 12,
    borderLeftWidth: 4,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 2,
  },
  progressDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.05)',
  },
  progressDetailLabel: {
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.2,
  },
  progressDetailValue: {
    fontSize: 14,
    fontWeight: '700',
  },
  currentChannel: {
    fontWeight: '800',
    textShadowColor: 'rgba(255, 215, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  jobsCount: {
    fontSize: 15,
    fontWeight: '800',
  },
  errorCount: {
    fontSize: 15,
    fontWeight: '800',
  },
  errorSection: {
    backgroundColor: '#FFF5F5',
    padding: 16,
    borderRadius: 12,
    marginTop: 16,
    borderWidth: 1,
    borderColor: '#FED7D7',
  },
  errorSectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
  },
  errorList: {
    maxHeight: 150,
  },
  errorItem: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingVertical: 4,
  },
  errorNumber: {
    fontSize: 12,
    fontWeight: '600',
    marginRight: 6,
    minWidth: 20,
  },
  errorText: {
    fontSize: 11,
    flex: 1,
    lineHeight: 16,
  },
  errorMore: {
    fontSize: 11,
    fontStyle: 'italic',
    marginTop: 4,
    textAlign: 'center',
  },
  progressPercent: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'right',
  },
  channelsSection: {
    marginBottom: 24,
  },
});